/**
 * AI pilot skill tiers.
 *
 * The four names are the game's own: the Mission Builder manual's pilot picker
 * reads *"For planes, choose Novice, Regular, or Veteran Pilots, or a Famous
 * Ace"* (docs/game/aces-squadrons.md §"Elite vs. ordinary"), and `SKILL.DAT`'s
 * 84-byte header plus 20-byte records ×19/×25 lines up one record per named ace
 * (docs/formats/missions-campaign.md). What each tier *is* numerically is not in
 * any source we have, so every field below is INFERRED and CALIBRATION-PENDING;
 * what is not negotiable is the ordering — each column is monotone across the
 * four tiers, which is what the tests pin.
 *
 * The three things skill changes are the three things that actually decide a
 * WWI dogfight: how well the pilot *aims*, how well they read the fight, and
 * how much of the manoeuvre vocabulary they can fly.
 *
 * ## Skill is aim quality, not scatter
 *
 * `dispersionScale` used to widen the random cone every one of this pilot's
 * rounds was thrown inside — a novice's Vickers physically shot worse than an
 * ace's. There is no such cone in the original (`traverse.ts`, `gunnery.ts`):
 * rounds fly dead straight out of every gun in the sky, and the only thing that
 * separates a novice from an ace is where the gun is pointing when the trigger
 * goes down. So the column became `aimErrorScale`, a multiplier on the angular
 * error in the pilot's own firing solution (`aiAimErrorRad`), and it acts by
 * moving the point the pilot flies the nose onto — which is the same mistake a
 * bad shot makes in a real aeroplane, and is visible from outside as a burst
 * that streaks past the target rather than a burst that sprays around it.
 */

import type { ManeuverId } from "./maneuvers.ts";

export const AI_SKILLS = ["novice", "regular", "veteran", "ace"] as const;

export type AiSkill = (typeof AI_SKILLS)[number];

export interface SkillProfile {
  readonly skill: AiSkill;
  /**
   * Multiplier on `aiAimErrorRad`, the angular error this pilot's firing
   * solution carries. 1 is the reference hand; a novice's 3.4 means the nose
   * settles a few degrees off where the target actually is.
   */
  readonly aimErrorScale: number;
  /** 0..1 of the intercept lead the pilot actually applies. */
  readonly leadQuality: number;
  /** Multiplier on `aiDecisionIntervalSeconds` — a novice is slow to react. */
  readonly decisionIntervalScale: number;
  /** Multiplier on the cone the pilot needs before opening fire. A novice fires wildly. */
  readonly firingConeScale: number;
  /** Multiplier on the range the pilot opens fire at. Good pilots close first. */
  readonly firingRangeScale: number;
  /** Multiplier on burst length — the manual's "the good pilots would fire short bursts". */
  readonly burstScale: number;
  /** 0..1: how long the pilot presses an attack before breaking off. */
  readonly aggression: number;
  /** Which manoeuvres this pilot can fly. A novice does not have a Split-S. */
  readonly maneuvers: readonly ManeuverId[];
  /** Which block of `EVADE.DAT`'s 28 rows this tier reads. See `evade-table.ts`. */
  readonly evadeBlock: number;
}

/** Every pilot can do these; the tiers add to them. */
const CORE_MANEUVERS: readonly ManeuverId[] = ["pursue", "extend", "breakTurn", "dive"];

const REGULAR_MANEUVERS: readonly ManeuverId[] = [...CORE_MANEUVERS, "leadTurn", "zoom", "slipTurn", "barrelRoll"];

const VETERAN_MANEUVERS: readonly ManeuverId[] = [...REGULAR_MANEUVERS, "splitS", "immelmann", "sideslip"];

const ACE_MANEUVERS: readonly ManeuverId[] = [...VETERAN_MANEUVERS, "loop", "halfLoop", "reverse"];

export const SKILL_PROFILES: Readonly<Record<AiSkill, SkillProfile>> = {
  novice: {
    skill: "novice",
    aimErrorScale: 3.4,
    leadQuality: 0.25,
    decisionIntervalScale: 1.8,
    firingConeScale: 2.6,
    firingRangeScale: 1.5,
    burstScale: 2.2,
    aggression: 0.35,
    maneuvers: CORE_MANEUVERS,
    evadeBlock: 0,
  },
  regular: {
    skill: "regular",
    aimErrorScale: 2.1,
    leadQuality: 0.55,
    decisionIntervalScale: 1.25,
    firingConeScale: 1.7,
    firingRangeScale: 1.2,
    burstScale: 1.5,
    aggression: 0.55,
    maneuvers: REGULAR_MANEUVERS,
    evadeBlock: 1,
  },
  veteran: {
    skill: "veteran",
    aimErrorScale: 1.4,
    leadQuality: 0.8,
    decisionIntervalScale: 0.9,
    firingConeScale: 1.2,
    firingRangeScale: 0.95,
    burstScale: 1.1,
    aggression: 0.75,
    maneuvers: VETERAN_MANEUVERS,
    evadeBlock: 2,
  },
  ace: {
    skill: "ace",
    aimErrorScale: 1,
    leadQuality: 1,
    decisionIntervalScale: 0.7,
    firingConeScale: 1,
    firingRangeScale: 0.8,
    burstScale: 0.85,
    aggression: 0.9,
    maneuvers: ACE_MANEUVERS,
    evadeBlock: 3,
  },
};

export function skillProfile(skill: AiSkill): SkillProfile {
  return SKILL_PROFILES[skill];
}

/** True when this pilot has the manoeuvre in their repertoire. */
export function canFly(profile: SkillProfile, id: ManeuverId): boolean {
  return profile.maneuvers.includes(id);
}
